/* Live Freight Tracking Map — /components/LiveFreightTrackingMap.jsx
 *
 * Lightweight SVG-based route map (no Mapbox/Google key required) that
 * plots the origin → destination polyline plus a pulsing "current" pin.
 * Renders the freight tracking response from `/api/shipping/freight-track/{id}`.
 *
 * Props:
 *  trail  : [{ lat, lng, city, state, hub }]     — full route polyline
 *  current: { lat, lng, city, state, hub }        — current position
 *  origin : { lat, lng, city, state }             — start
 *  destination: { lat, lng, city, state }         — end
 *  status : string                                — e.g. "in_transit"
 *  progressPct: number                            — 0..100
 *
 * Coordinates get projected using a simple US-scale equirectangular
 * projection into a 800×260 SVG viewBox.
 */
import { MapPin, Truck, CheckCircle2, AlertTriangle } from "lucide-react";

// US-wide equirectangular bounds (~continental US)
const US_BOUNDS = { minLat: 24.5, maxLat: 49.5, minLng: -125.0, maxLng: -66.0 };
const VB_W = 800;
const VB_H = 260;
const PAD  = 24;

function project(lat, lng, bounds = US_BOUNDS) {
  const w = VB_W - PAD * 2;
  const h = VB_H - PAD * 2;
  const x = PAD + ((lng - bounds.minLng) / (bounds.maxLng - bounds.minLng)) * w;
  const y = PAD + (1 - (lat - bounds.minLat) / (bounds.maxLat - bounds.minLat)) * h;
  return { x, y };
}

function boundsFor(points) {
  if (!points?.length) return US_BOUNDS;
  const lats = points.map((p) => p.lat).filter((v) => typeof v === "number");
  const lngs = points.map((p) => p.lng).filter((v) => typeof v === "number");
  if (!lats.length || !lngs.length) return US_BOUNDS;
  const pad = 3;
  return {
    minLat: Math.min(...lats) - pad,
    maxLat: Math.max(...lats) + pad,
    minLng: Math.min(...lngs) - pad,
    maxLng: Math.max(...lngs) + pad,
  };
}

export function LiveFreightTrackingMap({
  trail = [], current, origin, destination, status, progressPct = 0,
}) {
  // Auto-fit bounds to the full trail so smaller lanes fill the map.
  const bounds = boundsFor([...(trail || []), origin, destination, current].filter(Boolean));
  const pts = (trail || []).filter((p) => typeof p.lat === "number" && typeof p.lng === "number");
  const projected = pts.map((p) => ({ ...p, ...project(p.lat, p.lng, bounds) }));
  const cur = current && typeof current.lat === "number" ? project(current.lat, current.lng, bounds) : null;
  const orig = origin && typeof origin.lat === "number" ? project(origin.lat, origin.lng, bounds) : null;
  const dest = destination && typeof destination.lat === "number" ? project(destination.lat, destination.lng, bounds) : null;

  const polyline = projected.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const isDelivered = status === "delivered";
  const isException = status === "exception";

  return (
    <div className="rounded-2xl bg-secondary/40 border border-border overflow-hidden" data-testid="freight-track-map">
      <svg viewBox={`0 0 ${VB_W} ${VB_H}`} preserveAspectRatio="xMidYMid meet" className="w-full h-auto">
        {/* Faint reference grid */}
        <defs>
          <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgb(0,0,0)" strokeOpacity="0.05" strokeWidth="1"/>
          </pattern>
          <linearGradient id="routeGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="rgb(30,41,59)" stopOpacity="0.85"/>
            <stop offset="100%" stopColor="rgb(30,41,59)" stopOpacity="0.35"/>
          </linearGradient>
        </defs>
        <rect x="0" y="0" width={VB_W} height={VB_H} fill="url(#grid)"/>

        {/* Polyline — full route */}
        {projected.length > 1 && (
          <polyline
            points={polyline}
            fill="none"
            stroke="url(#routeGrad)"
            strokeWidth="2.5"
            strokeDasharray="6 4"
            strokeLinecap="round"
          />
        )}

        {/* Waypoint dots */}
        {projected.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="4" fill="white" stroke="rgb(30,41,59)" strokeWidth="1.5"/>
          </g>
        ))}

        {/* Origin pin */}
        {orig && (
          <g>
            <circle cx={orig.x} cy={orig.y} r="8" fill="rgb(16,185,129)" stroke="white" strokeWidth="2"/>
            <text x={orig.x} y={orig.y - 14} textAnchor="middle" fontSize="10" fill="rgb(30,41,59)" fontWeight="600">
              {origin.city || "Origin"}
            </text>
          </g>
        )}

        {/* Destination pin */}
        {dest && (
          <g>
            <circle cx={dest.x} cy={dest.y} r="8"
              fill={isDelivered ? "rgb(16,185,129)" : "rgb(30,41,59)"}
              stroke="white" strokeWidth="2"/>
            <text x={dest.x} y={dest.y - 14} textAnchor="middle" fontSize="10" fill="rgb(30,41,59)" fontWeight="600">
              {destination.city || "Destination"}
            </text>
          </g>
        )}

        {/* Current truck position (only when in transit) */}
        {cur && !isDelivered && (
          <g>
            {/* Pulse */}
            <circle cx={cur.x} cy={cur.y} r="14" fill="rgb(59,130,246)" fillOpacity="0.25">
              <animate attributeName="r" from="8" to="18" dur="1.6s" repeatCount="indefinite"/>
              <animate attributeName="fill-opacity" from="0.35" to="0" dur="1.6s" repeatCount="indefinite"/>
            </circle>
            <circle cx={cur.x} cy={cur.y} r="8" fill="rgb(59,130,246)" stroke="white" strokeWidth="2"/>
            <text x={cur.x} y={cur.y - 14} textAnchor="middle" fontSize="10" fill="rgb(30,41,59)" fontWeight="700">
              {current.city || "In transit"}
            </text>
          </g>
        )}
      </svg>

      {/* Progress bar under the map */}
      <div className="px-5 py-4 flex items-center gap-4 bg-white border-t border-border">
        <div className="flex-1">
          <div className="text-[10px] mono uppercase tracking-[0.16em] text-foreground/50 mb-1.5 flex items-center gap-1.5">
            {isDelivered ? (
              <><CheckCircle2 className="w-3 h-3 text-emerald-600"/>Delivered</>
            ) : isException ? (
              <><AlertTriangle className="w-3 h-3 text-amber-600"/>Exception</>
            ) : (
              <><Truck className="w-3 h-3 text-blue-600"/>In transit · {Math.round(progressPct || 0)}%</>
            )}
          </div>
          <div className="h-2 rounded-full bg-secondary overflow-hidden">
            <div
              data-testid="freight-track-progress-bar"
              className={`h-full ${isDelivered ? "bg-emerald-500" : isException ? "bg-amber-500" : "bg-blue-500"} transition-all`}
              style={{ width: `${Math.min(100, Math.max(0, progressPct || (isDelivered ? 100 : 0)))}%` }}
            />
          </div>
        </div>
        {current?.city && !isDelivered && (
          <div className="text-right shrink-0">
            <div className="text-[10px] mono uppercase tracking-[0.16em] text-foreground/50">Current</div>
            <div className="text-sm font-medium flex items-center gap-1"><MapPin className="w-3 h-3"/>{current.city}{current.state ? `, ${current.state}` : ""}</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default LiveFreightTrackingMap;
